import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useData } from "@/context/DataContext";
import { addDays, isoDate, parseISO, startOfWeek } from "@/lib/date";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { CaretLeft, CaretRight, TrendUp, CheckSquare, Clock, Sparkle } from "@phosphor-icons/react";

function fmtHours(mins) {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

export default function RecapPage() {
  const { catById } = useData();
  const [anchor, setAnchor] = useState(new Date());
  const [recap, setRecap] = useState(null);
  const [loading, setLoading] = useState(false);

  const weekStart = isoDate(startOfWeek(anchor));

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/recap/week?week_start=${weekStart}`);
        if (!cancelled) setRecap(data);
      } catch (err) {
        console.error("Kon weekterugblik niet ophalen:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [weekStart]);

  const rangeLabel = `${parseISO(weekStart).toLocaleDateString("nl-NL", { day: "numeric", month: "short" })} – ${addDays(parseISO(weekStart), 6).toLocaleDateString("nl-NL", { day: "numeric", month: "short", year: "numeric" })}`;

  const completion = recap ? Math.round(recap.completion_rate * 100) : 0;
  const byCat = recap ? Object.entries(recap.minutes_by_category || {}).sort((a, b) => b[1] - a[1]) : [];
  const totalMinutes = recap?.total_scheduled_minutes || 0;

  return (
    <div className="p-5 lg:p-8 max-w-5xl">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500">Zondagsamenvatting</div>
          <h1 className="text-3xl md:text-4xl font-semibold tracking-tight text-slate-50 mt-1">Weekterugblik</h1>
          <div className="text-sm text-slate-500 mt-1">{rangeLabel}</div>
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="recap-prev" variant="outline" size="icon" onClick={() => setAnchor(addDays(anchor, -7))} className="border-slate-800 bg-[#121C2F] hover:bg-slate-800 text-slate-300"><CaretLeft size={16} /></Button>
          <Button data-testid="recap-current" variant="outline" onClick={() => setAnchor(new Date())} className="border-slate-800 bg-[#121C2F] hover:bg-slate-800 text-slate-200 text-xs">Deze week</Button>
          <Button data-testid="recap-next" variant="outline" size="icon" onClick={() => setAnchor(addDays(anchor, 7))} className="border-slate-800 bg-[#121C2F] hover:bg-slate-800 text-slate-300"><CaretRight size={16} /></Button>
        </div>
      </div>

      {loading && !recap && (
        <div className="rounded-xl border border-slate-800 bg-[#121C2F] p-8 text-center text-slate-500">Terugblik laden…</div>
      )}

      {recap && (
        <div className="space-y-6">
          {/* Stat cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div data-testid="recap-stat-done" className="rounded-xl border border-slate-800 bg-[#121C2F] p-5">
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-500"><CheckSquare size={12} /> Taken klaar</div>
              <div className="text-3xl font-semibold text-slate-50 mt-1">{recap.tasks_done}</div>
              <div className="text-xs text-slate-500">of {recap.tasks_total} gepland</div>
              <Progress value={completion} className="h-1.5 bg-slate-800 mt-3" />
              <div className="text-[11px] text-slate-400 mt-1">{completion}% completion</div>
            </div>
            <div data-testid="recap-stat-slipped" className="rounded-xl border border-slate-800 bg-[#121C2F] p-5">
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-500"><TrendUp size={12} /> Uitgesteld</div>
              <div className="text-3xl font-semibold text-slate-50 mt-1">{recap.tasks_incomplete}</div>
              <div className="text-xs text-slate-500">nog open</div>
            </div>
            <div data-testid="recap-stat-scheduled" className="rounded-xl border border-slate-800 bg-[#121C2F] p-5">
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-500"><Clock size={12} /> Ingepland</div>
              <div className="text-3xl font-semibold text-slate-50 mt-1">{fmtHours(recap.total_scheduled_minutes)}</div>
              <div className="text-xs text-slate-500">over {recap.events_count} afspraken</div>
            </div>
            <div data-testid="recap-stat-upcoming" className="rounded-xl border border-slate-800 bg-[#121C2F] p-5">
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-500"><Sparkle size={12} /> Komt eraan</div>
              <div className="text-3xl font-semibold text-slate-50 mt-1">{recap.upcoming_important.length}</div>
              <div className="text-xs text-slate-500">belangrijke data volgende week</div>
            </div>
          </div>

          {/* Time by category */}
          <div className="rounded-xl border border-slate-800 bg-[#121C2F] p-5">
            <div className="text-xs uppercase tracking-widest text-slate-500 mb-3">Tijd per levensgebied</div>
            {byCat.length === 0 && <div className="text-sm text-slate-500">Geen afspraken deze week.</div>}
            <div className="space-y-2">
              {byCat.slice(0, 8).map(([cid, mins]) => {
                const cat = cid === "uncategorised" ? null : catById(cid);
                const pct = totalMinutes ? Math.round((mins / totalMinutes) * 100) : 0;
                const color = cat?.color || "#64748B";
                return (
                  <div key={cid}>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-slate-200 font-medium">{cat?.name || "Zonder categorie"}</span>
                      <span className="text-slate-400">{fmtHours(mins)} · {pct}%</span>
                    </div>
                    <div className="mt-1 h-1.5 rounded-full bg-slate-800 overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${pct}%`, background: color }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {/* Top incomplete */}
            <div className="rounded-xl border border-slate-800 bg-[#121C2F] p-5">
              <div className="text-xs uppercase tracking-widest text-slate-500 mb-3">Wat is blijven liggen</div>
              {recap.top_incomplete.length === 0 ? (
                <div className="text-sm text-slate-500">Niets openstaand. Goed werk!</div>
              ) : (
                <div className="space-y-2">
                  {recap.top_incomplete.map((t) => (
                    <div key={t.id} className="flex items-center justify-between text-sm">
                      <span className="text-slate-200 truncate">{t.title}</span>
                      <span className="text-[11px] text-slate-500 shrink-0 ml-3">{t.date}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Upcoming */}
            <div className="rounded-xl border border-slate-800 bg-[#121C2F] p-5">
              <div className="text-xs uppercase tracking-widest text-slate-500 mb-3">Hoogtepunten volgende week</div>
              {[...recap.upcoming_important, ...recap.upcoming_events].length === 0 ? (
                <div className="text-sm text-slate-500">Volgende week is nog open. Geniet van de ruimte.</div>
              ) : (
                <div className="space-y-2">
                  {recap.upcoming_important.map((i) => (
                    <div key={i.title + i.date} className="flex items-center justify-between text-sm">
                      <span className="text-rose-300 truncate">★ {i.title}</span>
                      <span className="text-[11px] text-slate-500 shrink-0 ml-3">{i.date}</span>
                    </div>
                  ))}
                  {recap.upcoming_events.slice(0, 6).map((e, i) => (
                    <div key={`${e.title}-${e.date}-${i}`} className="flex items-center justify-between text-sm">
                      <span className="text-slate-200 truncate">{e.title}</span>
                      <span className="text-[11px] text-slate-500 shrink-0 ml-3">{e.date} · {e.start_time}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
